import type {
    NonSkuLabGrownDiamondFilterInput,
    RingBuilderNaturalDiamondPresetInput,
    StoneClarityCode,
    StoneColorCode,
    StoneShapeCode,
} from '@/graphql/api'

/** Mount-only links land on this natural stone; it matches the `G+ / VS2+` preset. */
export const naturalDiamondDefault = {
    carat: '1.00',
    color: 'DIAMOND_G',
    clarity: 'DIAMOND_VS2',
} as const satisfies {
    carat: string
    color: StoneColorCode
    clarity: StoneClarityCode
}

export type NaturalDiamondPresetLike = {
    carat: string
    color: string
    clarity: string
}

/** `G+` → `G`, `DIAMOND_G` → `G`, `VS2+` → `VS2` */
export const normalizeNaturalGrade = (grade: string): string =>
    grade
        .toUpperCase()
        .replace(/^DIAMOND_/, '')
        .replace(/\+$/, '')

const caratsEqual = (a: string | number, b: string | number): boolean =>
    Math.abs(Number(a) - Number(b)) < 0.001

/** Nearest carat in either direction; ties go to the smaller carat. */
export const pickClosestCarat = (
    targetCarat: number,
    availableCarats: number[]
): number | null => {
    let closest: number | null = null

    for (const carat of [...availableCarats].sort((a, b) => a - b)) {
        if (
            closest === null ||
            Math.abs(carat - targetCarat) <
                Math.abs(closest - targetCarat) - 0.0001
        ) {
            closest = carat
        }
    }

    return closest
}

export const naturalPresetToFilter = (
    preset: NaturalDiamondPresetLike,
    shape: StoneShapeCode
): NonSkuLabGrownDiamondFilterInput => ({
    carat: Number(preset.carat).toFixed(2),
    color: `DIAMOND_${normalizeNaturalGrade(preset.color)}` as StoneColorCode,
    clarity: `DIAMOND_${normalizeNaturalGrade(
        preset.clarity
    )}` as StoneClarityCode,
    shape,
})

export const isNaturalPresetMatchingFilter = (
    preset: NaturalDiamondPresetLike,
    filter: NaturalDiamondPresetLike
): boolean =>
    caratsEqual(preset.carat, filter.carat) &&
    normalizeNaturalGrade(preset.color) ===
        normalizeNaturalGrade(filter.color) &&
    normalizeNaturalGrade(preset.clarity) ===
        normalizeNaturalGrade(filter.clarity)

/**
 * The preset input must use the API's exact strings (`1`, `1.50`, `G+`),
 * so the route filter is resolved against the ring's preset list.
 */
export const findNaturalPreset = <T extends NaturalDiamondPresetLike>(
    presets: readonly T[],
    filter: NaturalDiamondPresetLike
): T | null =>
    presets.find((preset) => isNaturalPresetMatchingFilter(preset, filter)) ??
    null

/**
 * Preset at the carat closest to `filter.carat`, keeping its grades when that
 * carat has them, otherwise the default grades.
 */
export const findClosestCaratNaturalPreset = <
    T extends NaturalDiamondPresetLike,
>(
    presets: readonly T[],
    filter: NaturalDiamondPresetLike
): T | null => {
    const carat = pickClosestCarat(
        Number(filter.carat),
        presets.map((preset) => Number(preset.carat))
    )

    if (carat === null) {
        return null
    }

    const atCarat = presets.filter((preset) => caratsEqual(preset.carat, carat))

    return (
        findNaturalPreset(atCarat, { ...filter, carat: String(carat) }) ??
        findNaturalPreset(atCarat, {
            ...naturalDiamondDefault,
            carat: String(carat),
        }) ??
        atCarat[0] ??
        null
    )
}

export const naturalPresetToInput = (
    preset: NaturalDiamondPresetLike,
    shape: StoneShapeCode
): RingBuilderNaturalDiamondPresetInput => ({
    carat: preset.carat,
    color: preset.color,
    clarity: preset.clarity,
    shape,
})

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    const presets = [
        { carat: '1', color: 'D', clarity: 'VVS2+' },
        { carat: '1', color: 'G+', clarity: 'VS2+' },
        { carat: '1.50', color: 'G+', clarity: 'VS2+' },
    ]

    describe('findNaturalPreset', () => {
        test('matches route codes against API preset strings', () => {
            expect(
                findNaturalPreset(presets, {
                    carat: '1.00',
                    color: 'DIAMOND_G',
                    clarity: 'DIAMOND_VS2',
                })
            ).toBe(presets[1])
        })

        test('compares carats numerically', () => {
            expect(
                findNaturalPreset(presets, {
                    carat: '1.5',
                    color: 'G',
                    clarity: 'VS2',
                })
            ).toBe(presets[2])
        })

        test('returns null for unknown combinations', () => {
            expect(
                findNaturalPreset(presets, {
                    carat: '1.00',
                    color: 'DIAMOND_D',
                    clarity: 'DIAMOND_VS2',
                })
            ).toBeNull()
        })
    })

    describe('findClosestCaratNaturalPreset', () => {
        test('keeps grades available at the closest carat', () => {
            expect(
                findClosestCaratNaturalPreset(presets, {
                    carat: '1.4',
                    color: 'G',
                    clarity: 'VS2',
                })
            ).toBe(presets[2])
        })

        test('falls back to the default grades from lab-grown ones', () => {
            expect(
                findClosestCaratNaturalPreset(presets, {
                    carat: '1.1',
                    color: 'F',
                    clarity: 'VS1',
                })
            ).toBe(presets[1])
        })
    })

    describe('naturalPresetToFilter', () => {
        test('maps preset strings to route codes', () => {
            expect(naturalPresetToFilter(presets[2]!, 'ROUND')).toStrictEqual({
                carat: '1.50',
                color: 'DIAMOND_G',
                clarity: 'DIAMOND_VS2',
                shape: 'ROUND',
            })
        })
    })
}
